import React, { useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, RotateCcw } from 'lucide-react';
import { TemporaryPasswordPanel } from './TemporaryPasswordPanel';
import { logError } from '@/lib/logger';

type ResetPatientPasswordModalProps = {
  patientId: number;
  patientName: string;
  trigger: React.ReactNode;
};

type ResetStatus = 'idle' | 'pending' | 'success' | 'error';

export function ResetPatientPasswordModal({
  patientId,
  patientName,
  trigger,
}: ResetPatientPasswordModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<ResetStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState('');

  const csrfToken = useMemo(() => {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content;
  }, []);

  const resetState = useCallback(() => {
    setStatus('idle');
    setErrorMessage(null);
    setTemporaryPassword('');
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetState();
    }
  };

  const handleResetPassword = async () => {
    if (status === 'pending') return;
    setStatus('pending');
    setErrorMessage(null);
    setTemporaryPassword('');

    try {
      const response = await fetch(`/therapists/patients/${patientId}/reset_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const extracted =
          (Array.isArray(data?.error) ? data.error.join(', ') : data?.error) ||
          'Impossibile resettare la password.';
        setErrorMessage(extracted);
        setStatus('error');
        return;
      }

      const data = (await response.json()) as { temporary_password?: string };
      setTemporaryPassword(data.temporary_password || '');
      setStatus('success');
    } catch (error) {
      logError('Errore reset password cliente', error);
      setErrorMessage('Errore di comunicazione col server. Riprova più tardi.');
      setStatus('error');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {React.isValidElement(trigger) ? trigger : <span>{trigger}</span>}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[480px]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Resetta password di {patientName}</DialogTitle>
          <DialogDescription>
            Genera una nuova password temporanea per il cliente. La password precedente smetterà di
            funzionare immediatamente.
          </DialogDescription>
        </DialogHeader>

        {status !== 'success' ? (
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Premi &quot;Resetta password&quot; per generare una credenziale nuova. Condividila con
              il cliente perché possa accedere nuovamente all&apos;app.
            </p>
            {errorMessage && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorMessage}
              </div>
            )}
            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button type="button" variant="ghost" disabled={status === 'pending'}>
                  Annulla
                </Button>
              </DialogClose>
              <Button type="button" onClick={handleResetPassword} disabled={status === 'pending'}>
                {status === 'pending' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Resettando…
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    Resetta password
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              Password aggiornata con successo. Comunica al cliente la nuova credenziale.
            </div>
            <TemporaryPasswordPanel
              password={temporaryPassword}
              helperText="Ricordagli di cambiarla al primo accesso per mantenere sicuro l'account."
              autoFocusCopyButton
            />
            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Chiudi
                </Button>
              </DialogClose>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ResetPatientPasswordModal;
